import api from './api';

/**
 * Upload an image with automatic content moderation
 * This is the ONLY way to upload images - ensures all images are validated
 * 
 * @param {File} file - The image file to upload
 * @param {string} folder - Optional folder in S3 (default: 'uploads')
 * @returns {Promise<{publicUrl: string, key: string, fileName: string}>}
 * @throws {Error} If image is inappropriate or upload fails
 */
export const uploadImage = async (file, folder = 'uploads') => {
    try {
        // Create FormData
        const formData = new FormData();
        formData.append('image', file);
        formData.append('folder', folder);

        // Upload with validation
        const response = await api.post('/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return {
            publicUrl: response.data.publicUrl,
            key: response.data.key,
            fileName: response.data.fileName,
            size: response.data.size,
            requiresManualReview: response.data.requiresManualReview || false
        };

    } catch (error) {
        // Handle specific errors
        if (error.response?.data?.error === 'inappropriate_content') {
            const details = error.response.data.details;
            const labels = details?.labels || [];
            const confidence = details?.confidence || 0;

            // Create a concise error message
            let errorMessage = '🚫 Imagem rejeitada: Conteúdo inapropriado detectado';

            if (labels.length > 0) {
                errorMessage += ` (${labels.join(', ')})`;
            }

            if (confidence > 0) {
                errorMessage += `. Confiança: ${confidence}%`;
            }

            errorMessage += '. Por favor, escolha outra imagem que esteja de acordo com nossas políticas.';

            const err = new Error(errorMessage);
            err.code = 'INAPPROPRIATE_CONTENT';
            err.details = { labels, confidence };
            throw err;
        }

        if (error.response?.data?.error === 'file_too_large') {
            const err = new Error('📦 Arquivo muito grande. Tamanho máximo: 10MB. Por favor, reduza o tamanho da imagem.');
            err.code = 'FILE_TOO_LARGE';
            throw err;
        }

        if (error.response?.data?.error === 'no_file') {
            const err = new Error('📁 Nenhum arquivo selecionado. Por favor, selecione uma imagem para enviar.');
            err.code = 'NO_FILE';
            throw err;
        }

        // Generic error
        const err = new Error(
            error.response?.data?.message ||
            '❌ Falha ao enviar imagem. Por favor, tente novamente.'
        );
        err.code = 'UPLOAD_FAILED';
        throw err;
    }
};

/**
 * Upload multiple images with validation
 * @param {File[]} files - Array of image files
 * @param {string} folder - Optional folder in S3
 * @returns {Promise<Array>} Array of upload results
 */
export const uploadMultipleImages = async (files, folder = 'uploads') => {
    const results = [];
    const errors = [];

    for (let i = 0; i < files.length; i++) {
        try {
            const result = await uploadImage(files[i], folder);
            results.push(result);
        } catch (error) {
            errors.push({
                fileName: files[i].name,
                error: error.message
            });
        }
    }

    return {
        successful: results,
        failed: errors,
        total: files.length,
        successCount: results.length,
        failureCount: errors.length
    };
};

/**
 * Legacy function name for backward compatibility
 * @deprecated Use uploadImage instead
 */
export const uploadFile = async (file, token, folder = 'uploads') => {
    // Ignore token parameter (not needed anymore, handled by api service)
    const result = await uploadImage(file, folder);
    return result.publicUrl; // Return just the URL for backward compatibility
};

const uploadService = {
    uploadImage,
    uploadMultipleImages,
    uploadFile // For backward compatibility
};

export default uploadService;
